import { makeVar, NetworkStatus, useQuery } from '@apollo/client';
import { useSession } from '@descope/react-sdk';
import { useEffect, useState } from 'react';
import { USER_ROLES, type UserRole } from '@constants/user-roles';
import { client } from '@services/graphql-client';

import GET_PORTAL_CONFIG_QUERY from './portal-config.query.graphql';

export const roleMap = {
  Admin: USER_ROLES.ADMIN,
  'Group Admin': USER_ROLES.GROUP,
  'Maine Physician (Read Only)': USER_ROLES.PHYSICIAN,
  Member: USER_ROLES.MEMBER,
  'MSS Admin (Read Only)': USER_ROLES.BUWELO,
  'Partner Admin': USER_ROLES.PARTNER,
  'Partner Admin (Read Only)': USER_ROLES.PARTNER,
  Pharmacy: USER_ROLES.PHARMACY,
  'Pharmacy Admin': USER_ROLES.PHARMACY,
  'SmithRx Admin': USER_ROLES.ADMIN,
  'SmithRx Super Admin': USER_ROLES.SUPER_ADMIN,
  SuperAdmin: USER_ROLES.SUPER_ADMIN,
} as const;

const readOnlyRoles = new Set([
  'Partner Admin (Read Only)',
  'MSS Admin (Read Only)',
  'Maine Physician (Read Only)',
]);

export const currentUserEmailVariable = makeVar<string | null>(null);

const ADMIN_ROLES = [USER_ROLES.ADMIN, USER_ROLES.SUPER_ADMIN];

interface UserState {
  isFetching: boolean;
  isLoggedIn: boolean;
  currentRole?: UserRole;
  currentRoles?: UserRole[];
  permissions?: string[];
  isMimicking?: boolean;
  memberId?: string;
  isReadOnly?: boolean;
  email?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  role?: string;
  roles?: string[];
}

export const useLoggedInUser = () => {
  const { isAuthenticated } = useSession();
  const [user, setUser] = useState<UserState>({
    isFetching: true,
    isLoggedIn: false,
  });

  const { data, error, networkStatus, refetch } = useQuery(
    GET_PORTAL_CONFIG_QUERY,
    {
      client,
    }
  );

  useEffect(() => {
    if (isAuthenticated) {
      void refetch();
    }
  }, [isAuthenticated, refetch]);

  useEffect(() => {
    if (data?.session) {
      currentUserEmailVariable(data.session.profile?.email);

      const isMimicking = Boolean(data.session.parent);

      const currentRole = roleMap[data.session.role as keyof typeof roleMap];
      const currentRoles = data.session.roles?.map(
        (item: string) => roleMap[item as keyof typeof roleMap]
      );

      setUser({
        ...data?.session,
        ...data?.session?.profile,
        currentRole,
        currentRoles,
        isFetching: false,
        isLoggedIn: Boolean(data),
        isMimicking,
        isReadOnly: readOnlyRoles.has(data?.session?.role),
        memberId: data?.session?.resources?.member?.[0],
        permissions: data.session.permissions,
      });
    }
  }, [data, isAuthenticated]);

  const isFetching = networkStatus === NetworkStatus.loading;

  if (!data && (user.isLoggedIn || user.isFetching !== isFetching)) {
    setUser({
      isFetching,
      isLoggedIn: false,
    });
  }

  const isAdmin = ADMIN_ROLES.some(role => user.currentRoles?.includes(role));
  const isSuperAdmin = user.currentRoles?.includes(USER_ROLES.SUPER_ADMIN);
  const isPharmacy = user.currentRoles?.includes(USER_ROLES.PHARMACY);

  return {
    ...user,
    error,
    isAdmin,
    isPharmacy,
    isSuperAdmin,
  };
};
